/**
 * Generated by orval v6.31.0 🍺
 * Do not edit manually.
 * Report Server API
 * Report Server API - 신고 관리 시스템

## 주요 기능
- 신고 생성 및 조회
- 신고 검토 및 처리 (관리자)
- 신고 통계 분석
- 우선순위 관리
- 증거 자료 관리
- 다양한 타입 지원 (코스, 사용자, 댓글, 게시글 등)

## 아키텍처
- Spring Data JPA (PostgreSQL)
- OAuth2 JWT 기반 인증
- 관리자 권한 기반 검토 시스템
- AI 모더레이션 점수 지원

## 신고 처리 플로우
1. PENDING (접수됨) → 신고 생성
2. REVIEWING (검토 중) → 관리자 검토 시작
3. RESOLVED (처리 완료) 또는 REJECTED (기각됨) → 최종 결정
4. APPEALED (항소됨) → 재검토 요청
5. CLOSED (종료됨) → 완전 종료

 * OpenAPI spec version: 1.0.0
 */
import { useQuery } from "@tanstack/react-query";
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  ErrorResponse,
  GetReportStatisticsParams,
  PendingCountResponse,
  ReportStatistics,
} from "./reportServerAPI.schemas";
import { customInstance } from "../custom-axios";
import type { ErrorType } from "../custom-axios";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * 지정된 기간 동안의 신고 통계를 조회합니다.
- 총 신고 건수
- 상태별 분포
- 대상 타입별 분포
- 신고 사유별 분포
- 일별 추세

 * @summary 신고 통계 조회
 */
export const getReportStatistics = (
  params: GetReportStatisticsParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<ReportStatistics>(
    { url: `/api/reports/statistics`, method: "GET", params, signal },
    options,
  );
};

export const getGetReportStatisticsQueryKey = (
  params: GetReportStatisticsParams,
) => {
  return [`/api/reports/statistics`, ...(params ? [params] : [])] as const;
};

export const getGetReportStatisticsQueryOptions = <
  TData = Awaited<ReturnType<typeof getReportStatistics>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetReportStatisticsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReportStatistics>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetReportStatisticsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReportStatistics>>
  > = ({ signal }) => getReportStatistics(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getReportStatistics>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetReportStatisticsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReportStatistics>>
>;
export type GetReportStatisticsQueryError = ErrorType<ErrorResponse>;

/**
 * @summary 신고 통계 조회
 */
export const useGetReportStatistics = <
  TData = Awaited<ReturnType<typeof getReportStatistics>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetReportStatisticsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReportStatistics>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetReportStatisticsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * 현재 PENDING 상태인 신고의 총 개수를 조회합니다.
관리자 대시보드에서 즉시 확인해야 할 신고 수를 표시할 때 사용합니다.

 * @summary 대기 중인 신고 개수 조회
 */
export const getPendingReportCount = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<PendingCountResponse>(
    { url: `/api/reports/pending/count`, method: "GET", signal },
    options,
  );
};

export const getGetPendingReportCountQueryKey = () => {
  return [`/api/reports/pending/count`] as const;
};

export const getGetPendingReportCountQueryOptions = <
  TData = Awaited<ReturnType<typeof getPendingReportCount>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getPendingReportCount>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPendingReportCountQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPendingReportCount>>
  > = ({ signal }) => getPendingReportCount(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPendingReportCount>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetPendingReportCountQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPendingReportCount>>
>;
export type GetPendingReportCountQueryError = ErrorType<ErrorResponse>;

/**
 * @summary 대기 중인 신고 개수 조회
 */
export const useGetPendingReportCount = <
  TData = Awaited<ReturnType<typeof getPendingReportCount>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getPendingReportCount>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetPendingReportCountQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
